import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useTranslation } from 'react-i18next';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import StudentTab from './components/per-student/StudentTab';
import ExerciseTab from './components/ExerciseTab';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ResultsDataContextProvider } from './context/ResultsDataContext';
import ContentHeader from '../../../layout/content/ContentHeader';
import ContentBody from '../../../layout/content/ContentBody';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import { FilterContextProvider } from './context/filter-context/FilterContext';
import { FilterSearchParam } from '../../../shared-components/elementis/types/FilterSearchParam';
import TABS, { Tab } from '../../../shared-components/elementis/types/ResultsPageTabs';

interface ResultsProps extends WithScrollThresholdProps {}

function Results({ pastThreshold }: ResultsProps) {
  const { t } = useTranslation();

  useDocumentTitle(t('PAGES.RESULTS.DOCUMENT_TITLE'));

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = (searchParams.get('selectedTab') || TABS.STUDENT) as Tab;

  const onTabChange = useCallback(
    (tab: string) => {
      if (tab === selectedTab) return;

      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set('selectedTab', tab);

        if (tab === TABS.EXERCISE) {
          newParams.delete(FilterSearchParam.Groups);
        }

        return newParams;
      });
    },
    [selectedTab, setSearchParams],
  );

  return (
    <Page>
      <Tabs selectedTab={selectedTab} onTabChange={onTabChange}>
        <ContentHeader
          cover={
            <Page.Header
              density={pastThreshold ? 'compact' : 'normal'}
              headerText={t('PAGES.RESULTS.PAGE_TITLE')}
              extra={
                <Tabs.TabList aria-label={t('PAGES.RESULTS.TAB_LIST.A11Y_LABEL')} size={pastThreshold ? 'sm' : 'md'}>
                  <Tabs.Tab id={TABS.STUDENT}>{t('PAGES.RESULTS.TAB_LIST.TABS.STUDENTS')}</Tabs.Tab>
                  <Tabs.Tab id={TABS.EXERCISE}>{t('PAGES.RESULTS.TAB_LIST.TABS.EXERCISE')}</Tabs.Tab>
                </Tabs.TabList>
              }
            />
          }
        />
        <ContentBody>
          <ResultsDataContextProvider>
            <FilterContextProvider selectedTab={selectedTab}>
              <Tabs.TabsContent>
                <Tabs.TabPanel id={TABS.STUDENT}>
                  <StudentTab />
                </Tabs.TabPanel>
                <Tabs.TabPanel id={TABS.EXERCISE}>
                  <ExerciseTab />
                </Tabs.TabPanel>
              </Tabs.TabsContent>
            </FilterContextProvider>
          </ResultsDataContextProvider>
        </ContentBody>
      </Tabs>
    </Page>
  );
}

export default withScrollThreshold(Results);
